import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ChatSidebar from 'components/chat/ChatSidebar';
import ChatContent from 'components/chat/ChatContent';
import { HistoryProvider, useHistory } from 'context/HistoryContext';
import { useChatHistorySync } from 'hooks/useChatHistorySync';
import useChatStore from 'stores/chatStore';
import useAuthStore from 'stores/authStore';

interface LocationState {
  uploadedFile?: File;
  message?: string;
  skipTariff?: boolean;
  conversationId?: string;
}

const ChatInner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendMessage, processingStatus, reset, setSkipTariff, loadConversation } = useChatStore();
  const { getUserId } = useAuthStore();
  const { triggerRefetch } = useHistory();
  const hasUploadedRef = useRef<boolean>(false);
  const hasLoadedConvRef = useRef<boolean>(false);

  // Sync chat store events with history refetching
  useChatHistorySync();

  // Handle conversationId passed via location state (e.g. from Results page)
  useEffect(() => {
    const state = location.state as LocationState;
    const conversationId = state?.conversationId;
    const userId = getUserId();

    if (conversationId && userId && !hasLoadedConvRef.current) {
      hasLoadedConvRef.current = true;
      loadConversation(conversationId, userId);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, loadConversation, getUserId, navigate, location.pathname]);

  useEffect(() => {
    const state = location.state as LocationState;
    const uploadedFile = state?.uploadedFile;
    const message = state?.message;
    const skipTariff = state?.skipTariff;

    if ((uploadedFile || message) && !hasUploadedRef.current) {
      hasUploadedRef.current = true;
      reset();
      setSkipTariff(skipTariff ?? true);
      sendMessage(message || '', uploadedFile, { skipTariff: skipTariff ?? true });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, sendMessage, navigate, location.pathname, reset, setSkipTariff]);

  const handleNewClassification = useCallback(() => {
    reset();
    hasUploadedRef.current = false;

    // Trigger history refetch after starting new conversation
    triggerRefetch();

    // Navigate to landing page
    navigate('/');
  }, [reset, triggerRefetch, navigate]);

  const handleSendMessage = useCallback(
    async (message: string, file?: File, options?: { skipTariff?: boolean }, additionalFiles?: File[]) => {
      await sendMessage(message, file, options, additionalFiles);
    },
    [sendMessage]
  );

  return (
    <div className="chat-page gradient-bg min-h-screen">
      <div className="gradient-content h-full">
        <ChatSidebar onNewClassification={handleNewClassification} />
        <ChatContent onSendMessage={handleSendMessage} processingStatus={processingStatus} />
      </div>
    </div>
  );
};

const Chat: React.FC = () => {
  return (
    <HistoryProvider>
      <ChatInner />
    </HistoryProvider>
  );
};

export default Chat;
